import { useId, useState } from 'react'
import PropTypes from 'prop-types'

import DDIcon from '../../icons/DDIcon'
import styles from './TextInput.module.css'

// Spec §5.2 — label above (required marked with a red *), single-line input, helper text
// below that turns red + gets a warning icon on error; a success state (account number)
// shows a green border + check instead of helper text.
const TextInput = ({
  label,
  required,
  value,
  onChange,
  onBlur,
  placeholder,
  error,
  success,
  helperText,
  type,
  name,
  id,
  disabled,
  maxLength,
  inputMode,
  autoComplete,
}) => {
  const generatedId = useId()
  const inputId = id ?? generatedId
  const helperId = `${inputId}-helper`
  const [focused, setFocused] = useState(false)

  const rowClasses = [
    styles.inputRow,
    focused && styles.focused,
    error && styles.error,
    success && styles.success,
    disabled && styles.disabled,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={styles.wrapper}>
      {label && (
        <label htmlFor={inputId} className={styles.label}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </label>
      )}
      <div className={rowClasses}>
        <input
          id={inputId}
          name={name}
          type={type}
          className={styles.input}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={maxLength}
          inputMode={inputMode}
          autoComplete={autoComplete}
          aria-invalid={Boolean(error)}
          aria-describedby={error || helperText ? helperId : undefined}
          onChange={onChange}
          onFocus={() => setFocused(true)}
          onBlur={(e) => {
            setFocused(false)
            onBlur?.(e)
          }}
        />
        {success && <DDIcon name='verified-fill' />}
      </div>
      {(error || helperText) && (
        <div id={helperId} className={[styles.helper, error && styles.errorText].filter(Boolean).join(' ')}>
          <DDIcon name={error ? 'helper-error' : 'helper-warning'} />
          <span>{error || helperText}</span>
        </div>
      )}
    </div>
  )
}

TextInput.propTypes = {
  label: PropTypes.string,
  required: PropTypes.bool,
  value: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  placeholder: PropTypes.string,
  error: PropTypes.string,
  success: PropTypes.bool,
  helperText: PropTypes.string,
  type: PropTypes.string,
  name: PropTypes.string,
  id: PropTypes.string,
  disabled: PropTypes.bool,
  maxLength: PropTypes.number,
  inputMode: PropTypes.string,
  autoComplete: PropTypes.string,
}

TextInput.defaultProps = {
  type: 'text',
  required: false,
  success: false,
  disabled: false,
}

export default TextInput
